import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Sparkles, Loader2, Wand2, Lock, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * AI Section Generator — Iter 198f: slide-in side panel (was full-screen
 * modal). Docks to the right edge so the canvas stays fully visible while
 * the vendor writes their prompt. Backdrop is a subtle scrim (not opaque)
 * so the canvas is always inspectable.
 */
export default function AIGenerateModal({ open, onClose, onInsert, mode = 'section' /* 'section' | 'site' */ }) {
  const [prompt, setPrompt] = useState('');
  const [businessDesc, setBusinessDesc] = useState('');
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('warm and authentic');
  const [sections, setSections] = useState(7);
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  // Iter 198m — Inspire-from-URL sub-mode. When active, we scrape a reference
  // URL for structure/palette and ask Claude to generate ORIGINAL copy
  // tailored to the vendor's business. We never reproduce the source copy.
  const [inspireMode, setInspireMode] = useState(false);
  const [inspireUrl, setInspireUrl] = useState('');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/builder/ai/credits`, { headers })
      .then(r => setCredits(r.data))
      .catch(() => {});
  }, [open]);

  if (!open) return null;

  const cost = mode === 'site' ? 5 : 1;
  const insufficient = credits && credits.credits_remaining < cost;

  const upgrade = async () => {
    try {
      const r = await axios.post(`${API}/builder/ai/upgrade-tier`, { tier: 'pro' }, { headers });
      toast.success(`Upgraded to Pro · ${r.data.monthly_credits} credits/mo`);
      const fresh = await axios.get(`${API}/builder/ai/credits`, { headers });
      setCredits(fresh.data);
    } catch (e) {
      toast.error('Upgrade failed');
    }
  };

  const generate = async () => {
    setErrMsg(null);
    setLoading(true);
    try {
      if (inspireMode) {
        // Iter 198m — URL-inspired generation. Separate endpoint, no credits
        // for now (we audit via builder_inspire_jobs collection).
        const r = await axios.post(
          `${API}/builder/ai/inspire-from-url`,
          { url: inspireUrl, business_description: businessDesc, business_name: topic || undefined },
          { headers },
        );
        onInsert(r.data.blocks || []);
        toast.success(`${r.data.blocks?.length || 0} inspired section${r.data.blocks?.length === 1 ? '' : 's'} added — fully editable, original copy for your business`);
        setInspireUrl(''); setBusinessDesc('');
        onClose();
        return;
      }
      const url = mode === 'site' ? '/builder/ai/generate-site' : '/builder/ai/generate-section';
      const body = mode === 'site'
        ? { business_description: businessDesc, topic, tone, sections }
        : { prompt, topic, tone };
      const r = await axios.post(`${API}${url}`, body, { headers });
      onInsert(r.data.blocks || []);
      setCredits(c => c ? { ...c, credits_used: c.monthly_credits - r.data.credits_remaining, credits_remaining: r.data.credits_remaining } : c);
      toast.success(`${r.data.blocks?.length || 0} section${r.data.blocks?.length === 1 ? '' : 's'} generated · ${r.data.credits_remaining} credits left`);
      setPrompt(''); setBusinessDesc('');
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        setErrMsg('Out of credits this month. Upgrade for 100 generations/month.');
      } else {
        setErrMsg(typeof detail === 'string' ? detail : 'Generation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <>
      {/* Subtle scrim — click to close, but canvas remains visible */}
      <div
        className="fixed inset-0 z-[200] bg-black/25 backdrop-blur-[2px] transition-opacity duration-200"
        onClick={onClose}
        data-testid="ai-generate-scrim"
      />
      {/* Slide-in panel — docked right */}
      <div
        className="fixed top-0 right-0 bottom-0 z-[210] w-full max-w-[560px] flex flex-col bg-[rgba(14,14,35,0.98)] border-l border-white/[0.08] shadow-[-20px_0_60px_-10px_rgba(0,0,0,0.6)] backdrop-blur-2xl animate-[slide-in-right_240ms_cubic-bezier(0.22,1,0.36,1)]"
        data-testid="ai-generate-modal"
      >
      <style>{`@keyframes slide-in-right { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#A78BFA]" />
          <span className="text-[11px] uppercase tracking-[0.16em] text-white font-bold">
            {mode === 'site' ? 'AI Website Generator' : 'AI Section Generator'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#A78BFA] font-bold">
            {credits ? `${Math.max(0, credits.credits_remaining)}/${credits.monthly_credits}` : '…'}
          </span>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-white/50 hover:text-white hover:bg-white/[0.06] flex items-center justify-center"
            data-testid="ai-close"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div>
          <div className="mb-6">
            <div className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] items-center justify-center mb-3 shadow-[0_6px_20px_rgba(139,92,246,0.35)]">
              {inspireMode ? <LinkIcon size={20} className="text-white" /> : <Wand2 size={20} className="text-white" />}
            </div>
            <h2 className="text-[22px] font-bold text-white font-syne tracking-tight leading-tight">
              {inspireMode ? 'Inspired by a site you love' : mode === 'site' ? 'Generate your homepage' : 'Generate a section'}
            </h2>
            <p className="text-[#9CA3AF] text-[12px] mt-1.5 leading-relaxed">
              {inspireMode
                ? 'Paste the URL of a site whose layout you like. We\'ll match the structure, palette, and font pairing — then write original copy for your business and drop fully-editable sections into your editor.'
                : mode === 'site'
                ? 'Describe your business and we\'ll build a 7-section homepage tuned to your brand voice.'
                : 'Describe the section you want — hero, about, services, testimonial — and we\'ll write the copy and pick a layout.'}
            </p>
            {/* Iter 198m — mode selector pill group */}
            <div className="flex items-center gap-1 mt-4 p-0.5 rounded-lg bg-white/[0.03] border border-white/[0.06] w-fit" data-testid="ai-mode-tabs">
              <button
                onClick={() => { setInspireMode(false); }}
                className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${!inspireMode ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow' : 'text-white/50 hover:text-white'}`}
                data-testid="ai-mode-ai"
              >Describe it</button>
              <button
                onClick={() => setInspireMode(true)}
                className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${inspireMode ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow' : 'text-white/50 hover:text-white'}`}
                data-testid="ai-mode-inspire"
              >Inspire from URL</button>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-5">
            {inspireMode ? (
              <>
                <Field label="Reference URL" hint="Any public site. We extract structure, palette, and fonts — never copy.">
                  <input
                    type="url"
                    value={inspireUrl}
                    onChange={(e) => setInspireUrl(e.target.value)}
                    placeholder="https://examplebrand.com"
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
                    data-testid="ai-inspire-url"
                    autoFocus
                  />
                </Field>
                <Field label="What does your business do?" hint="So we write copy for YOU, not the reference site">
                  <textarea
                    value={businessDesc}
                    onChange={(e) => setBusinessDesc(e.target.value)}
                    placeholder="I run an Indigenous-owned beadwork studio in Tulsa. I make custom regalia pieces and teach workshops to youth..."
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white placeholder:text-[#64748B] h-28 focus:outline-none focus:border-[#8B5CF6]/40"
                    data-testid="ai-inspire-biz"
                  />
                </Field>
                <Field label="Business name (optional)">
                  <input
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="Kindred Studios"
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-[13px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
                    data-testid="ai-inspire-name"
                  />
                </Field>
                <div className="flex items-start gap-2 text-[11px] text-white/50 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <Sparkles size={11} className="text-[#A78BFA] mt-0.5 shrink-0" />
                  <div>
                    <strong className="text-white/70">Inspired-by, not identical.</strong> We match the layout pattern, color palette, and font pairing. Every word of text and every image is written/picked fresh for your business and is fully editable.
                  </div>
                </div>
              </>
            ) : mode === 'site' ? (
              <>
                <Field label="What does your business do?" hint="The clearer this is, the better the result">
                  <textarea
                    value={businessDesc}
                    onChange={(e) => setBusinessDesc(e.target.value)}
                    placeholder="I run an Indigenous-owned beadwork studio in Tulsa. I make custom regalia pieces and teach workshops to youth..."
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white placeholder:text-[#64748B] h-32 focus:outline-none focus:border-[#8B5CF6]/40"
                    data-testid="ai-business-desc"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Topic">
                    <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Beadwork & Craft" className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-[13px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40" data-testid="ai-topic" />
                  </Field>
                  <Field label="Brand voice">
                    <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-[13px] text-white focus:outline-none focus:border-[#8B5CF6]/40" data-testid="ai-tone">
                      <option>warm and authentic</option>
                      <option>bold and confident</option>
                      <option>quiet and poetic</option>
                      <option>playful and friendly</option>
                      <option>professional and clear</option>
                    </select>
                  </Field>
                </div>
                <Field label="Number of sections">
                  <input type="range" min={3} max={12} value={sections} onChange={(e) => setSections(parseInt(e.target.value))} className="w-full accent-[#A78BFA]" data-testid="ai-sections" />
                  <p className="text-[11px] text-[#9CA3AF] mt-1">{sections} sections</p>
                </Field>
              </>
            ) : (
              <>
                <Field label="Describe the section" hint="One or two sentences. Include the vibe, content, and any CTA.">
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="A calm hero with my craft story and a CTA to book a consultation"
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-[14px] text-white placeholder:text-[#64748B] h-28 focus:outline-none focus:border-[#8B5CF6]/40"
                    autoFocus
                    data-testid="ai-prompt"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Topic (optional)">
                    <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Beadwork & Craft" className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-[13px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40" data-testid="ai-topic" />
                  </Field>
                  <Field label="Brand voice">
                    <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-[13px] text-white focus:outline-none focus:border-[#8B5CF6]/40" data-testid="ai-tone">
                      <option>warm and authentic</option>
                      <option>bold and confident</option>
                      <option>quiet and poetic</option>
                      <option>playful and friendly</option>
                      <option>professional and clear</option>
                    </select>
                  </Field>
                </div>
              </>
            )}

            {errMsg && (
              <div className="bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 rounded-lg p-3 text-[12.5px] text-[#FCA5A5]" data-testid="ai-error">
                {errMsg}
              </div>
            )}

            {insufficient && (
              <div className="bg-gradient-to-r from-[#8B5CF6]/15 to-[#3B82F6]/15 border border-[#8B5CF6]/30 rounded-xl p-5" data-testid="ai-upgrade-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#A78BFA]/20 flex items-center justify-center shrink-0">
                    <Lock size={16} className="text-[#A78BFA]" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-bold text-[14px]">Upgrade to Pro · $75/month</h4>
                    <p className="text-[#9CA3AF] text-[12px] mt-1 leading-relaxed">
                      100 AI generations per month · priority model · unlimited section regenerations
                    </p>
                  </div>
                  <button onClick={upgrade} className="px-4 py-2 rounded-lg bg-white text-[#0E0E23] text-[11px] font-bold uppercase tracking-wider hover:bg-[#F5F5F5]" data-testid="ai-upgrade-btn">
                    Upgrade
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center pt-3">
              <p className="text-[11px] text-[#64748B]">Costs {cost} credit{cost === 1 ? '' : 's'} · Powered by GPT-5.2</p>
              <button
                onClick={generate}
                disabled={loading || insufficient || (mode === 'site' ? !businessDesc.trim() : !prompt.trim())}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-[13px] font-bold uppercase tracking-wider hover:shadow-[0_0_24px_rgba(139,92,246,0.5)] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                data-testid="ai-generate-btn"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {loading ? 'Generating…' : (mode === 'site' ? 'Generate homepage' : 'Generate section')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>,
    document.body
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-[#9CA3AF] mb-1.5">{label}</label>
      {hint && <p className="text-[11.5px] text-[#64748B] mb-2">{hint}</p>}
      {children}
    </div>
  );
}
